import { useMemo, useRef } from 'react';
import { Box, Tabs, TabList, Tab } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import FormBase from 'components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { customTabStyles, selectedTabStyles } from '@/constants';
import { tabContentStyles } from '../constants';
import ReadOnlyProfileField from '../formFields/ReadOnlyProfileField';
import {
    useGetConsent,
    useGetConsentHistory,
    useGetOCProfile,
} from '../queries';
import { mapConsentRow } from '../utils';

const ConsentView = () => {
    const { profileId, consentId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { darkMode } = useSelector((state) => state.settings);

    const tabParam = searchParams.get('tab') || 'consent-details';
    const tabIndex = tabParam === 'consent-history' ? 1 : 0;

    const { data: profile } = useGetOCProfile(profileId);
    const { data: consent, isLoading: isLoadingConsent } =
        useGetConsent(consentId);
    const { data: historyData, isLoading: isLoadingHistory } =
        useGetConsentHistory(consentId, tabIndex === 1);

    const contactsGridRef = useRef(null);
    const historyGridRef = useRef(null);

    const handleTabChange = (index) => {
        setSearchParams({
            tab: index === 1 ? 'consent-history' : 'consent-details',
        });
    };

    const backLink = `/operator-connect-profiles/${profileId}?tab=consents`;

    const breadCrumbTrail = [
        {
            label: 'All Operator Profiles',
            path: '/operator-connect-profiles',
        },
        {
            label: profile?.name || profileId,
            path: `/operator-connect-profiles/${profileId}?tab=consents`,
        },
        {
            label: 'Consents',
            path: undefined,
        },
    ];

    const contactColumnDefs = useMemo(
        () => [
            { headerName: 'Full Name', field: 'fullName' },
            { headerName: 'Email', field: 'email' },
            { headerName: 'Phone', field: 'phoneNumber' },
            { headerName: 'Company Name', field: 'companyName' },
            { headerName: 'Company Size', field: 'companySize' },
        ],
        [],
    );

    const historyColumnDefs = useMemo(
        () => [
            { headerName: 'Tenant ID', field: 'tenantID' },
            { headerName: 'Status', field: 'status' },
            { headerName: 'Company Name', field: 'defaultContact.companyName' },
            {
                headerName: 'Consented Countries',
                field: 'consentedCountriesString',
            },
            { headerName: 'Contact Name', field: 'defaultContact.fullName' },
            { headerName: 'Email', field: 'defaultContact.email' },
            { headerName: 'Phone', field: 'defaultContact.phoneNumber' },
            { headerName: 'Last Modified On', field: 'lastModifiedOn' },
            { headerName: 'Company Size', field: 'defaultContact.companySize' },
        ],
        [],
    );

    const historyRows = useMemo(
        () => historyData?.map(mapConsentRow) ?? [],
        [historyData],
    );

    const contactTableData = useMemo(
        () => ({
            columns: contactColumnDefs,
            data: consent?.contacts || [],
        }),
        [contactColumnDefs, consent],
    );

    const historyTableData = useMemo(
        () => ({ columns: historyColumnDefs, data: historyRows }),
        [historyColumnDefs, historyRows],
    );

    if (isLoadingConsent || !consent) {
        return <LoadingIndicator />;
    }

    const historyTabCardStyles =
        tabIndex === 1
            ? {
                  '.chakra-card': {
                      bg: 'transparent',
                      boxShadow: 'none',
                      border: 'none',
                      padding: 0,
                      maxWidth: 'none',
                  },
              }
            : {};

    return (
        <Box sx={{ ...tabContentStyles, ...historyTabCardStyles }}>
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                initialValues={{}}
                finalFormSubmit={() => {}}
                backLink={backLink}
                hideSaveButton
                tabMode={false}
                subTitleHeader
                subTitleHeaderComponent={
                    <Tabs
                        index={tabIndex}
                        onChange={handleTabChange}
                        variant="unstyled">
                        <TabList borderBottom={0} mb="10px">
                            <Tab
                                sx={customTabStyles}
                                _selected={selectedTabStyles}>
                                Consent Details
                            </Tab>
                            <Tab
                                sx={customTabStyles}
                                _selected={selectedTabStyles}>
                                Consent History
                            </Tab>
                        </TabList>
                    </Tabs>
                }>
                {() => {
                    if (tabIndex === 1) {
                        return (
                            <AGGridMain
                                gridRef={historyGridRef}
                                tableData={historyTableData}
                                titleName="Consent History"
                                isLoading={isLoadingHistory}
                                useFixedHeight={false}
                                domLayout={
                                    historyRows.length > 0
                                        ? 'autoHeight'
                                        : 'normal'
                                }
                                customHeight={
                                    historyRows.length === 0 ? '300px' : 'auto'
                                }
                            />
                        );
                    }

                    return [
                        <ReadOnlyProfileField
                            key="id"
                            label="ID"
                            value={consent.id}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="msid"
                            label="MSID"
                            value={consent.msid}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="tenantID"
                            label="Tenant ID"
                            value={consent.tenantID}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="tenantOrigin"
                            label="Tenant Origin"
                            value={consent.tenantOrigin}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="operatorID"
                            label="Operator ID"
                            value={consent.operatorID}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="status"
                            label="Status"
                            value={consent.status}
                            darkMode={darkMode}
                        />,
                        <AGGridMain
                            key="contacts-table"
                            gridRef={contactsGridRef}
                            tableData={contactTableData}
                            titleName="Contacts"
                            isLoading={isLoadingConsent}
                            useFixedHeight={false}
                            domLayout="autoHeight"
                            customHeight="auto"
                        />,
                    ];
                }}
            </FormBase>
        </Box>
    );
};

export default ConsentView;
